import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  MdDashboard,
  MdDescription,
  MdPeople,
  MdMic,
  MdFolder,
  MdPayment,
  MdBarChart,
  MdAccessTime,
  MdPersonAdd,
  MdSupervisorAccount,
  MdWork,
  MdEventAvailable,
  MdAssessment,
  MdSchedule,
  MdLogout,
  MdBusiness,
  MdSecurity,
  MdHistory,
  MdAssignment,
  MdVideoLibrary,
  MdContacts,
  MdOutlineReportProblem,
  MdBadge
} from 'react-icons/md';

/**
 * LeftSidebar Component
 * Icon rail navigation with module icons
 */
const LeftSidebar = ({ isCollapsed, isMobileOpen, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [expandedModules, setExpandedModules] = React.useState({});

  // Auto-expand modules if user is on a sub-page
  React.useEffect(() => {
    const dutyPaths = ['/hro/duty-management', '/hro/shift-management', '/hro/shift-assignment', '/hro/attendance-correction'];
    const leavePaths = ['/hro/leave-requests', '/hro/leave-policy', '/hro/leave-balance-assignment', '/hr/leave', '/hr/my-leave-balances'];
    const reportPaths = ['/chairman/reports', '/chairman/corrections', '/reports', '/chairman/assessment'];
    const dailyReportPaths = ['/hr/daily-report', '/chairman/daily-reports'];

    if (dutyPaths.some(path => location.pathname.startsWith(path))) {
      setExpandedModules(prev => ({ ...prev, duty: true }));
    }

    if (leavePaths.some(path => location.pathname.startsWith(path))) {
      setExpandedModules(prev => ({ ...prev, leave: true }));
    }

    if (reportPaths.some(path => location.pathname.startsWith(path))) {
      setExpandedModules(prev => ({ ...prev, reports: true }));
    }

    if (dailyReportPaths.some(path => location.pathname.startsWith(path))) {
      setExpandedModules(prev => ({ ...prev, dailyReport: true }));
    }
  }, [location.pathname]);

  // Define navigation modules with role-based access
  const modules = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: MdDashboard,
      path: getDashboardPath(user?.role),
      roles: ['CANDIDATE', 'ADMIN', 'HR', 'HRO', 'CHAIRMAN']
    },
    {
      id: 'assessment',
      label: 'Assessment',
      icon: MdAssessment,
      path: '/chairman/assessment',
      roles: ['CHAIRMAN']
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: MdDescription,
      path: user?.role === 'CANDIDATE' ? '/candidate/applications' : '/hr/applications',
      roles: ['CANDIDATE', 'ADMIN', 'HR', 'CHAIRMAN']
    },
    {
      id: 'candidates',
      label: 'Candidates',
      icon: MdPeople,
      path: '/admin/candidates',
      roles: ['ADMIN', 'HR', 'CHAIRMAN']
    },
    {
      id: 'interviews',
      label: 'Calendar',
      icon: MdMic,
      path: '/hr/interviews',
      roles: ['ADMIN', 'HR', 'CHAIRMAN']
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: MdFolder,
      path: user?.role === 'CANDIDATE' ? '/candidate/documents' : '/admin/documents',
      roles: ['CANDIDATE', 'HR', 'CHAIRMAN']
    },
    {
      id: 'attendance',
      label: 'Attendance',
      icon: MdAccessTime,
      path: ['HRO', 'CHAIRMAN'].includes(user?.role) ? '/hro/attendance' : '/hr/attendance',
      roles: ['HR', 'HRO', 'CHAIRMAN']
    },
    {
      id: 'leave',
      label: 'Leave Management',
      icon: MdEventAvailable,
      path: user?.role === 'HR' ? '/hr/leave' : '/hro/leave-requests',
      roles: ['HR', 'HRO', 'ADMIN', 'CHAIRMAN'],
      subItems: user?.role === 'HR' ? [
        { label: 'My Leaves', path: '/hr/leave' },
        { label: 'My Leave Balances', path: '/hr/my-leave-balances' }
      ] : [
        { label: 'Leave Requests', path: '/hro/leave-requests' },
        { label: 'Leave Policy', path: '/hro/leave-policy' },
        { label: 'Leave Balances', path: '/hro/leave-balance-assignment' },
      ]
    },
    {
      id: 'duty',
      label: 'Duty Management',
      icon: MdSchedule,
      path: '/hro/duty-management',
      roles: ['HRO', 'CHAIRMAN'],
      subItems: [
        { label: 'Duty Overview', path: '/hro/duty-management' },
        { label: 'Shift Management', path: '/hro/shift-management' },
        { label: 'Shift Assignment', path: '/hro/shift-assignment' },
        { label: 'Attendance Correction', path: '/hro/attendance-correction' }
      ]
    },
    {
      id: 'create-user',
      label: 'Create Users',
      icon: MdPersonAdd,
      path: '/admin/create-user',
      roles: ['ADMIN', 'CHAIRMAN'],
      subItems: [
        { label: 'Basic User Creation', path: '/admin/create-user' },
        { label: 'Complete Employee Profile', path: '/admin/create-complete-employee' }
      ]
    },
    {
      id: 'manage-users',
      label: 'Manage Users',
      icon: MdSupervisorAccount,
      path: '/admin/users',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'security',
      label: 'Security',
      icon: MdSecurity,
      path: '/admin/security',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'job-management',
      label: 'Job Management',
      icon: MdWork,
      path: '/admin/job-management',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'tutorial-videos',
      label: 'Tutorial Videos',
      icon: MdVideoLibrary,
      path: '/admin/tutorial-videos',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'id-card',
      label: 'ID Card',
      icon: MdBadge,
      path: '/admin/id-card',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: MdPayment,
      path: '/payments',
      roles: ['ADMIN', 'HR', 'HRO', 'CHAIRMAN']
    },
    {
      id: 'resignations',
      label: 'Resignations',
      icon: MdLogout,
      path: '/admin/resignations',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'daily-report',
      label: 'Daily Report',
      icon: MdAssignment,
      path: '/hr/daily-report',
      roles: ['HR']
    },
    {
      id: 'grievances',
      label: 'Grievances',
      icon: MdOutlineReportProblem,
      path: user?.role === 'CANDIDATE' ? '/candidate/grievances' : '/admin/grievances',
      roles: ['CANDIDATE', 'ADMIN', 'CHAIRMAN', 'HR', 'HRO']
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: MdBarChart,
      path: '/reports',
      roles: ['ADMIN', 'HRO', 'CHAIRMAN'],
      subItems: [
        { label: 'Summary', path: '/reports/summary' },
        { label: 'HR Performance', path: '/reports/hr-performance' },
        { label: 'Interviews', path: '/reports/interviews' },
        { label: 'Exams', path: '/reports/exams' },
        { label: 'Payments', path: '/reports/payments' },
        ...(user?.role === 'CHAIRMAN' ? [{ label: 'Departure', path: '/chairman/reports/departure' }] : []),
        ...(user?.role === 'CHAIRMAN' ? [{ label: 'Correction', path: '/chairman/corrections' }] : [])
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      icon: MdPayment,
      path: '/chairman/finance',
      roles: ['CHAIRMAN']
    },
    {
      id: 'contacts',
      label: 'Contacts',
      icon: MdContacts,
      path: '/admin/contacts',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'office-transactions',
      label: 'Office Transactions',
      icon: MdBusiness,
      path: user?.role === 'CHAIRMAN' ? '/chairman/office-transactions' : '/admin/office-transactions',
      roles: ['ADMIN', 'CHAIRMAN']
    },
    {
      id: 'chairman-logs',
      label: 'Logs',
      icon: MdHistory,
      path: '/chairman/logs',
      roles: ['CHAIRMAN']
    }
  ];

  // Filter modules based on user role
  const visibleModules = modules.filter(module =>
    module.roles.includes(user?.role)
  );

  const isActive = (path, subItems = []) => {
    if (location.pathname.startsWith(path)) {
      return true;
    }
    // Check if any sub-item is active
    if (subItems && subItems.length > 0) {
      return subItems.some(subItem => location.pathname.startsWith(subItem.path));
    }
    return false;
  };

  const handleModuleClick = (module) => {
    if (module.subItems && module.subItems.length > 0 && !isCollapsed) {
      // Toggle dropdown for modules with sub-items
      setExpandedModules(prev => ({
        ...prev,
        [module.id]: !prev[module.id]
      }));
    } else {
      // Navigate to the module path
      navigate(module.path);
    }
  };

  const handleSubItemClick = (subItem) => {
    navigate(subItem.path);
    if (window.innerWidth < 768) {
      onCloseMobile();
    }
  };

  const sidebarWidthClass = isCollapsed ? 'md:w-16' : 'md:w-60';
  const mobileStateClass = isMobileOpen ? 'translate-x-0' : '-translate-x-full';

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-screen w-72 flex-col border-r border-white/10 bg-charcoal text-beige shadow-xl transition-transform duration-300 ease-out md:relative md:z-10 md:h-full md:translate-x-0 md:shadow-none ${sidebarWidthClass} ${mobileStateClass}`}
    >
      <div className="flex items-center justify-center gap-3 border-b border-white/10 px-4 py-5 md:py-4">
        <img
          src="/logo_final.png"
          alt="Recruit Logo"
          className={`w-full h-auto object-contain ${isCollapsed ? 'max-w-[40px]' : 'max-w-[100px]'}`}
        />
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <div className="flex flex-col gap-1">
          {visibleModules.map(module => (
            <div key={module.id}>
              <button
                className={`flex items-center gap-3 py-3 px-4 bg-transparent border-none rounded-md text-beige cursor-pointer transition-all text-left w-full ${isActive(module.path, module.subItems) ? 'bg-olive text-white' : 'hover:bg-white/[0.08]'}`}
                onClick={() => {
                  handleModuleClick(module);
                  if ((!module.subItems || module.subItems.length === 0 || isCollapsed) && window.innerWidth < 768) {
                    onCloseMobile();
                  }
                }}
                title={module.label}
              >
                <module.icon className="text-xl min-w-[24px] text-center" size={22} />
                {!isCollapsed && (
                  <>
                    <span className="text-sm font-medium whitespace-nowrap overflow-hidden flex-1">{module.label}</span>
                    {module.subItems && module.subItems.length > 0 && (
                      <span className={`text-xs transition-transform ${expandedModules[module.id] ? 'rotate-90' : ''}`}>
                        ▶
                      </span>
                    )}
                  </>
                )}
              </button>

              {/* Sub-items dropdown */}
              {module.subItems && module.subItems.length > 0 && !isCollapsed && expandedModules[module.id] && (
                <div className="ml-6 mt-1 space-y-1">
                  {module.subItems.map((subItem, index) => (
                    <button
                      key={index}
                      className={`flex items-center gap-2 py-2 px-3 bg-transparent border-none rounded-md text-beige cursor-pointer transition-all text-left w-full text-sm ${location.pathname === subItem.path ? 'bg-olive/70 text-white' : 'hover:bg-white/[0.05]'}`}
                      onClick={() => handleSubItemClick(subItem)}
                      title={subItem.label}
                    >
                      <span className="w-2 h-2 bg-current rounded-full opacity-60"></span>
                      <span className="text-xs font-medium">{subItem.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>
    </aside>
  );
};

// Helper function to get dashboard path based on role
function getDashboardPath(role) {
  switch (role) {
    case 'CANDIDATE':
      return '/candidate/dashboard';
    case 'HR':
      return '/hr/dashboard';
    case 'HRO':
      return '/hro/dashboard';
    case 'CHAIRMAN':
      return '/chairman/dashboard';
    case 'ADMIN':
    default:
      return '/admin/dashboard';
  }
}

export default LeftSidebar;
